<template>
  <div class="flex flex-col gap-2">
    <label v-if="label" :for="inputId" class="font-medium text-text-primary text-[14px]">
      {{ label }}
      <span v-if="required" class="text-red">*</span>
    </label>
    <div class="flex gap-3 justify-start">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="el => setInputRef(el, index)"
        :id="`${inputId}-${index}`"
        :value="digit"
        :disabled="disabled"
        :readonly="readonly"
        type="text"
        maxlength="1"
        autocomplete="one-time-code"
        class="w-14 h-14 text-center text-[20px] font-semibold border-2 rounded-xl transition-colors duration-200 bg-white focus:outline-none"
        style="font-size: 20px"
        :class="{
          'border-primary': focusedIndex === index,
          'border-red': !!error && focusedIndex === index,
          'border-border': focusedIndex !== index && !error,
          'opacity-50 cursor-not-allowed': disabled || readonly,
        }"
        @input="handleInput(index, $event)"
        @paste="handlePaste($event)"
        @keydown="handleKeydown(index, $event)"
        @focus="focusedIndex = index"
        @blur="handleBlur"
      />
    </div>
    <p v-if="hint && !error" class="text-[14px] text-text-muted m-0">
      {{ hint }}
    </p>
    <span v-if="error" :id="`${inputId}-error`" class="text-red text-[14px]" role="alert">
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'

interface Props {
  modelValue: string
  label?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  error?: string | undefined
  hint?: string
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  readonly: false,
  required: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const inputRefs = ref<(HTMLInputElement | null)[]>([])
const focusedIndex = ref<number | null>(null)
const digits = ref<string[]>(['', '', '', ''])

const inputId = computed(() => props.id || `otp-input-${Math.random().toString(36).substr(2, 9)}`)

const setInputRef = (el: unknown, index: number): void => {
  if (el && el instanceof HTMLInputElement) {
    inputRefs.value[index] = el
  }
}

const getOtpValue = (): string => {
  return digits.value.join('')
}

const updateModelValue = (): void => {
  const value = getOtpValue()
  emit('update:modelValue', value)
}

const focusInput = (index: number): void => {
  nextTick(() => {
    const input = inputRefs.value[index]
    if (input) {
      input.focus()
      input.select()
    }
  })
}

const handleInput = (index: number, event: Event): void => {
  const target = event.target as HTMLInputElement
  let value = target.value.replace(/\D/g, '')

  if (value.length > 1) {
    const digitsArray = value.slice(0, 4).split('')
    digits.value = [...digitsArray, '', '', '', ''].slice(0, 4)
    updateModelValue()

    const nextEmptyIndex = digits.value.findIndex((d, i) => i > index && d === '')
    if (nextEmptyIndex !== -1) {
      focusInput(nextEmptyIndex)
    } else if (index < 3) {
      focusInput(3)
    }
    return
  }

  digits.value[index] = value
  updateModelValue()

  if (value && index < 3) {
    focusInput(index + 1)
  }
}

const handlePaste = (event: ClipboardEvent): void => {
  event.preventDefault()
  const pastedData = event.clipboardData?.getData('text') || ''
  const cleaned = pastedData.replace(/\D/g, '').slice(0, 4)

  if (cleaned.length === 0) return

  const digitsArray = cleaned.split('')
  digits.value = [...digitsArray, '', '', '', ''].slice(0, 4)
  updateModelValue()

  const nextEmptyIndex = digits.value.findIndex(d => d === '')
  if (nextEmptyIndex !== -1) {
    focusInput(nextEmptyIndex)
  } else {
    focusInput(3)
  }
}

const handleKeydown = (index: number, event: KeyboardEvent): void => {
  if (event.key === 'Backspace') {
    if (digits.value[index]) {
      digits.value[index] = ''
      updateModelValue()
    } else if (index > 0) {
      digits.value[index - 1] = ''
      updateModelValue()
      focusInput(index - 1)
    }
  } else if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault()
    focusInput(index - 1)
  } else if (event.key === 'ArrowRight' && index < 3) {
    event.preventDefault()
    focusInput(index + 1)
  } else if (event.key === 'Delete') {
    digits.value[index] = ''
    updateModelValue()
  }
}

const handleBlur = (): void => {
  focusedIndex.value = null
  emit('blur')
}

watch(
  () => props.modelValue,
  newValue => {
    if (newValue !== getOtpValue()) {
      const value = String(newValue || '')
        .replace(/\D/g, '')
        .slice(0, 4)
      const digitsArray = value.split('')
      digits.value = [...digitsArray, '', '', '', ''].slice(0, 4)
    }
  },
  { immediate: true }
)
</script>
